/**
 * windowFrameOptions — pure UI-facing helpers for `WindowFramePicker`: which
 * bound KINDS are composable for the start/end dropdown given the other
 * bound's current value, and how to clamp a same-band numeric offset so the
 * frame stays within the total order defined by `frameBoundRank`
 * (`overBodySpec.ts`). Kept as a sibling so `WindowFramePicker.tsx` stays a
 * thin rendering layer and this logic is independently unit-testable
 * (Task §4b-i-fix — closes the ordering-invalid-frame gap).
 */
import type { SqlEngine } from '../../../../sql-grammar/model';
import { frameBoundRank } from './overBodySpec';
import type { FrameBound, FrameBoundKind, FrameUnit } from './overBodySpec';

/**
 * Whether numeric-offset frame bounds (`N PRECEDING` / `N FOLLOWING`) are legal
 * for the given unit, ORDER-BY key COUNT and engine — the gate that keeps a
 * guided edit from turning a valid frame into engine-invalid SQL (Item I4 /
 * Lane C-2). The count matters, not just presence:
 *   • ROWS   — always (offsets count physical rows; no ORDER BY required).
 *   • RANGE  — requires EXACTLY ONE ORDER BY key (PG raises 42P20, and MySQL /
 *              Oracle / SQLite likewise reject 0 or ≥2 keys) AND an engine that
 *              supports RANGE numeric offsets (SQL Server does not — see
 *              `supportsRangeOffsetFrame`).
 *   • GROUPS — requires an ORDER BY (≥1 key); GROUPS *engine* support is a
 *              separate concern surfaced by `supportsGroupsFrame` + its note.
 * When this returns false the picker withholds the numeric-offset bound kinds
 * (and a unit switch demotes any existing numeric bound).
 */
export function numericOffsetsAllowed(unit: FrameUnit, orderByKeyCount: number, engine: SqlEngine): boolean {
  if (unit === 'rows') return true;
  if (unit === 'range') return orderByKeyCount === 1 && supportsRangeOffsetFrame(engine);
  return orderByKeyCount >= 1; // groups
}

/**
 * RANGE numeric-offset bounds (`RANGE N PRECEDING/FOLLOWING`) are accepted by
 * PostgreSQL, MySQL, Oracle and SQLite, but NOT by SQL Server — an mssql RANGE
 * frame allows only UNBOUNDED / CURRENT ROW, never a numeric offset. The engine
 * is known at edit time, so this is a fact (mirrors `supportsGroupsFrame`),
 * surfaced as a truthful note rather than a version hedge (Lane C-2).
 */
export function supportsRangeOffsetFrame(engine: SqlEngine): boolean {
  return engine !== 'mssql';
}

/**
 * GROUPS frame mode is supported by PostgreSQL, Oracle and SQLite, but NOT by
 * SQL Server or MySQL. The engine is known at edit time, so this is a fact —
 * used to surface a truthful "not supported on <engine>" note rather than the
 * version-hedge that only fits genuinely version-gated capabilities (Item I4).
 */
export function supportsGroupsFrame(engine: SqlEngine): boolean {
  return engine !== 'mssql' && engine !== 'mysql';
}

/** Strip the numeric-offset bound kinds from a candidate list. */
export function withoutNumericOffsets(kinds: readonly FrameBoundKind[]): FrameBoundKind[] {
  return kinds.filter((k) => k !== 'n-preceding' && k !== 'n-following');
}

/**
 * Coerce a numeric-offset bound to the nearest offset-free bound so switching a
 * frame to RANGE/GROUPS while no ORDER BY exists never emits an invalid numeric
 * offset. A start demotes to UNBOUNDED PRECEDING and an end to CURRENT ROW,
 * which stay ordering-valid together (Item I4).
 */
export function demoteNumericBound(bound: FrameBound, role: 'start' | 'end'): FrameBound {
  if (bound.kind !== 'n-preceding' && bound.kind !== 'n-following') return bound;
  return role === 'start' ? { kind: 'unbounded-preceding' } : { kind: 'current-row' };
}

/** The rank range a bound KIND can reach across its valid offsets (ignoring
 *  the concrete offset) — e.g. `n-preceding` ranges from -∞ (huge offset) up
 *  to -1 (offset 0), always strictly below CURRENT ROW's rank 0. */
function kindRankExtent(kind: FrameBoundKind): { readonly min: number; readonly max: number } {
  switch (kind) {
    case 'unbounded-preceding':
      return { min: Number.NEGATIVE_INFINITY, max: Number.NEGATIVE_INFINITY };
    case 'n-preceding':
      return { min: Number.NEGATIVE_INFINITY, max: -1 };
    case 'current-row':
      return { min: 0, max: 0 };
    case 'n-following':
      return { min: 1, max: Number.POSITIVE_INFINITY };
    case 'unbounded-following':
      return { min: Number.POSITIVE_INFINITY, max: Number.POSITIVE_INFINITY };
  }
}

/** A frame's end may be omitted (`ROWS 5 PRECEDING`), meaning implicit CURRENT
 *  ROW — treat that as a `current-row` bound for ordering purposes. */
export function endOrImplicitCurrentRow(end: FrameBound | null): FrameBound {
  return end ?? { kind: 'current-row' };
}

/** END-bound kinds composable given the current START bound (candidates whose
 *  best-case rank still reaches at or past the start's rank). */
export function reachableEndKinds(
  kinds: readonly FrameBoundKind[],
  start: FrameBound,
): FrameBoundKind[] {
  const startRank = frameBoundRank(start);
  return kinds.filter((k) => kindRankExtent(k).max >= startRank);
}

/** True iff the implicit-CURRENT-ROW end option is composable given `start`. */
export function reachableImplicitEnd(start: FrameBound): boolean {
  return kindRankExtent('current-row').max >= frameBoundRank(start);
}

/** START-bound kinds composable given the current END bound (or implicit
 *  CURRENT ROW when the frame has no explicit end yet). */
export function reachableStartKinds(
  kinds: readonly FrameBoundKind[],
  end: FrameBound | null,
): FrameBoundKind[] {
  const endRank = frameBoundRank(endOrImplicitCurrentRow(end));
  return kinds.filter((k) => kindRankExtent(k).min <= endRank);
}

/**
 * Clamp a same-band numeric offset so `BETWEEN start AND end` stays valid.
 * Only `n-preceding`-vs-`n-preceding` and `n-following`-vs-`n-following` pairs
 * produce a real numeric constraint (larger PRECEDING offset = earlier, so the
 * END offset must not exceed the START offset; larger FOLLOWING offset = later,
 * so the END offset must not be less than the START offset). Also floors to a
 * non-negative integer — a typed negative or fractional offset is invalid SQL.
 */
export function clampFrameOffset(
  kind: 'n-preceding' | 'n-following',
  desiredN: number,
  other: FrameBound,
  role: 'start' | 'end',
): number {
  const n = Math.max(0, Math.floor(Number.isFinite(desiredN) ? desiredN : 0));
  if (other.kind !== kind) return n;
  const otherN = other.n ?? 0;
  if (kind === 'n-preceding') return role === 'start' ? Math.max(n, otherN) : Math.min(n, otherN);
  return role === 'start' ? Math.min(n, otherN) : Math.max(n, otherN);
}
